import { useState, useEffect, useCallback } from 'react'
import { useAccount, useReadContract, useWriteContract, useChainId, useSwitchChain, usePublicClient } from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { parseAbi, parseEther, formatEther, keccak256, toBytes } from 'viem'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { useToast } from '../../../context/ToastContext'
import { Icon } from '../../../components/Layout'
import { getAllAdsStats } from '../../../services/marketplace'

const MARKET_ADDRESS = import.meta.env.VITE_AD_MARKETPLACE_ADDRESS
const SEPOLIA_ID     = 11155111

const MARKET_ABI = parseAbi([
  'struct Ad { address advertiser; bytes32 placementId; uint256 durationDays; string metadataURI; uint8 payToken; uint256 amountPaid; uint8 status; bool refunded; bool withdrawn; }',
  'struct Placement { uint256 ethPrice; uint256 cltPrice; bool exists; }',
  'function setPlacementPrice(bytes32 placementId, uint256 ethPrice, uint256 cltPrice) external',
  'function disablePlacement(bytes32 placementId) external',
  'function approveAd(uint256 adId) external',
  'function rejectAd(uint256 adId) external',
  'function withdrawAd(uint256 adId) external',
  'function withdrawAllEth() external',
  'function withdrawAllClt() external',
  'function getAd(uint256 adId) external view returns (Ad)',
  'function getPlacement(bytes32 placementId) external view returns (Placement)',
  'function placementIdFromString(string calldata name) external pure returns (bytes32)',
  'function nextAdId() external view returns (uint256)',
  'function ethEscrowed() external view returns (uint256)',
  'function cltEscrowed() external view returns (uint256)',
  'function owner() external view returns (address)',
])

const PLACEMENTS = [
  { name: 'feed',   label: 'Main Feed' },
  { name: 'banner', label: 'Marketplace Banner' },
]

const STATUS_LABEL = ['Pending', 'Approved', 'Rejected']
const STATUS_COLOR = [
  'text-yellow-500 bg-yellow-500/10 border-yellow-500/30',
  'text-green-500 bg-green-500/10 border-green-500/30',
  'text-error bg-error/10 border-error/30',
]

function placementId(name) {
  return keccak256(toBytes(name))
}

function fmt(wei) {
  if (wei == null) return '0'
  return parseFloat(formatEther(wei)).toFixed(4).replace(/\.?0+$/, '')
}

function fmtClt(wei) {
  if (wei == null) return '0'
  const n = Number(formatEther(wei))
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toFixed(2)
}

function WalletStatusBar({ isOwner, owner }) {
  return (
    <div className="flex items-center justify-between gap-3 border-4 border-on-background bg-surface-container-low px-5 py-3 flex-wrap"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px]">account_balance_wallet</span>
        <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">
          {isOwner ? 'Connected as owner' : 'Wallet'}
        </p>
      </div>
      <ConnectButton.Custom>
        {({ account, chain, openConnectModal, openAccountModal, openChainModal, mounted }) => {
          if (!mounted) return null
          const connected = !!account
          const wrongChain = connected && (chain?.unsupported || chain?.id !== SEPOLIA_ID)
          return (
            <div className="flex items-center gap-2">
              {connected && (
                <button
                  type="button"
                  onClick={openChainModal}
                  className={`px-3 py-1.5 border-2 font-bold text-[10px] uppercase tracking-widest transition-colors ${
                    wrongChain
                      ? 'border-error bg-error text-on-error hover:opacity-90'
                      : 'border-primary-container bg-primary-container/20 text-on-primary-container'
                  }`}
                >
                  {wrongChain ? `Wrong Network: ${chain?.name ?? '?'}` : chain?.name ?? 'Sepolia'}
                </button>
              )}
              <button
                type="button"
                onClick={connected ? openAccountModal : openConnectModal}
                className="flex items-center gap-2 px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors"
              >
                {connected ? (account.displayName ?? account.address) : 'Connect Wallet'}
              </button>
            </div>
          )
        }}
      </ConnectButton.Custom>
    </div>
  )
}

function NotDeployedBanner() {
  return (
    <div className="border-4 border-dashed border-on-background/20 p-10 text-center flex flex-col gap-3 items-center">
      <span className="material-symbols-outlined text-[40px] opacity-30">construction</span>
      <p className="font-extrabold uppercase tracking-widest text-[13px]">Marketplace contract not connected</p>
      <p className="text-[12px] text-on-surface-variant">Set VITE_AD_MARKETPLACE_ADDRESS in your Vercel env and redeploy.</p>
    </div>
  )
}

function WrongNetworkBanner() {
  const { switchChain, isPending } = useSwitchChain()
  return (
    <div className="border-4 border-error bg-error-container/20 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div>
        <p className="font-extrabold text-[13px] uppercase tracking-widest text-on-error-container">Wrong Network</p>
        <p className="text-[12px] text-on-surface-variant mt-1">Connect on <strong>Ethereum Sepolia</strong> to manage the ad marketplace.</p>
      </div>
      <button onClick={() => switchChain({ chainId: SEPOLIA_ID })} disabled={isPending}
        className="px-5 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 disabled:opacity-50 transition-opacity border-2 border-on-background shrink-0">
        {isPending ? 'Switching…' : 'Switch to Sepolia'}
      </button>
    </div>
  )
}

function WalletGate() {
  return (
    <div className="border-4 border-dashed border-on-background/20 p-10 text-center flex flex-col items-center gap-4">
      <span className="material-symbols-outlined text-[40px] opacity-30">account_balance_wallet</span>
      <p className="font-bold text-[13px] uppercase tracking-widest text-on-surface-variant">Connect the owner wallet to manage ads</p>
      <ConnectButton.Custom>
        {({ openConnectModal, mounted }) => {
          if (!mounted) return null
          return (
            <button onClick={openConnectModal}
              className="px-6 py-3 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity border-2 border-on-background"
              style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
              Connect Wallet
            </button>
          )
        }}
      </ConnectButton.Custom>
    </div>
  )
}

function NotOwnerBanner({ owner, address }) {
  return (
    <div className="border-4 border-error bg-error-container/20 p-5 flex flex-col gap-1">
      <p className="font-extrabold text-[13px] uppercase tracking-widest text-on-error-container">Connected wallet is not the contract owner</p>
      <p className="text-[11px] text-on-surface-variant font-mono break-all">Connected: {address}</p>
      <p className="text-[11px] text-on-surface-variant font-mono break-all">Owner: {owner ?? '—'}</p>
      <p className="text-[12px] text-on-surface-variant mt-1">You can still view ads below, but approve/reject/withdraw/pricing actions will revert on-chain until you connect the owner wallet.</p>
    </div>
  )
}

function PlacementPricingCard({ placement, isOwner, onSaved }) {
  const pid = placementId(placement.name)
  const { writeContractAsync } = useWriteContract()
  const chainId = useChainId()
  const { switchChainAsync } = useSwitchChain()
  const toast = useToast()
  const publicClient = usePublicClient({ chainId: SEPOLIA_ID })
  const [editing, setEditing] = useState(false)
  const [ethPrice, setEthPrice] = useState('')
  const [cltPrice, setCltPrice] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const { data: p, refetch } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'getPlacement',
    args: [pid],
    query: { enabled: !!MARKET_ADDRESS },
  })

  const startEdit = () => {
    setEthPrice(p?.ethPrice ? formatEther(p.ethPrice) : '')
    setCltPrice(p?.cltPrice ? formatEther(p.cltPrice) : '')
    setError(null)
    setEditing(true)
  }

  const ensureSepolia = async () => {
    if (chainId === SEPOLIA_ID) return true
    try {
      await switchChainAsync({ chainId: SEPOLIA_ID })
      return true
    } catch {
      toast.error('Switch your wallet to Ethereum Sepolia first.')
      return false
    }
  }

  const save = async () => {
    if (!(await ensureSepolia())) return
    setSaving(true)
    setError(null)
    try {
      const hash = await writeContractAsync({
        address: MARKET_ADDRESS,
        abi: MARKET_ABI,
        functionName: 'setPlacementPrice',
        args: [pid, parseEther(ethPrice || '0'), parseEther(cltPrice || '0')],
      })
      if (publicClient) await publicClient.waitForTransactionReceipt({ hash })
      setEditing(false)
      await refetch()
      onSaved?.()
      toast.success(`${placement.label} price updated.`)
    } catch (e) {
      const msg = e.shortMessage ?? e.message ?? 'Failed to save price'
      setError(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const disable = async () => {
    if (!(await ensureSepolia())) return
    setSaving(true)
    setError(null)
    try {
      const hash = await writeContractAsync({
        address: MARKET_ADDRESS,
        abi: MARKET_ABI,
        functionName: 'disablePlacement',
        args: [pid],
      })
      if (publicClient) await publicClient.waitForTransactionReceipt({ hash })
      await refetch()
      onSaved?.()
      toast.success(`${placement.label} disabled.`)
    } catch (e) {
      const msg = e.shortMessage ?? e.message ?? 'Failed to disable placement'
      setError(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border-4 border-on-background bg-surface p-5 flex flex-col gap-3"
      style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
      <div className="flex items-center justify-between">
        <p className="font-extrabold text-[14px]">{placement.label}</p>
        {p?.exists ? (
          <span className="px-2 py-0.5 border-2 border-primary-container bg-primary-container/20 text-on-primary-container text-[10px] font-bold uppercase tracking-widest rounded">Priced</span>
        ) : (
          <span className="px-2 py-0.5 border-2 border-on-surface-variant/40 text-on-surface-variant text-[10px] font-bold uppercase tracking-widest rounded">Not priced</span>
        )}
      </div>

      {error && <AdminAlert type="error" message={error} />}

      {editing ? (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">ETH Price</label>
              <input type="number" step="0.0001" min="0" value={ethPrice} onChange={e => setEthPrice(e.target.value)}
                placeholder="0.05"
                className="bg-surface border-2 border-on-background px-2.5 py-2 font-mono text-[13px] focus:outline-none focus:border-primary-container" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">CLT Price</label>
              <input type="number" step="1" min="0" value={cltPrice} onChange={e => setCltPrice(e.target.value)}
                placeholder="1000"
                className="bg-surface border-2 border-on-background px-2.5 py-2 font-mono text-[13px] focus:outline-none focus:border-primary-container" />
            </div>
          </div>
          <p className="text-[10px] text-on-surface-variant">Set either to 0 to disable that payment method. Both can't be 0.</p>
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} disabled={saving}
              className="flex-1 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={save} disabled={saving || !isOwner}
              className="flex-1 py-2 bg-on-background text-surface font-bold text-[10px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[12px] text-on-surface-variant font-mono">
            {p ? `${fmt(p.ethPrice)} ETH · ${fmtClt(p.cltPrice)} CLT` : 'Loading…'}
          </p>
          <div className="flex gap-2">
            <button onClick={startEdit} disabled={!isOwner}
              className="flex-1 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors disabled:opacity-50 rounded">
              Edit Price
            </button>
            {p?.exists && (
              <button onClick={disable} disabled={saving || !isOwner}
                className="flex-1 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-50 rounded">
                Disable
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function AdRow({ ad, isOwner, onChanged }) {
  const { writeContractAsync } = useWriteContract()
  const chainId = useChainId()
  const { switchChainAsync } = useSwitchChain()
  const toast = useToast()
  const publicClient = usePublicClient({ chainId: SEPOLIA_ID })
  const [busy, setBusy] = useState(null) // 'approve' | 'reject' | 'withdraw' | null
  const [error, setError] = useState(null)

  let meta = {}
  try { meta = JSON.parse(atob(ad.metadataURI?.split(',')[1] ?? '')) } catch {}

  const placementLabel = PLACEMENTS.find(p => placementId(p.name) === ad.placementId)?.label ?? 'Unknown placement'

  const ensureSepolia = async () => {
    if (chainId === SEPOLIA_ID) return true
    try {
      await switchChainAsync({ chainId: SEPOLIA_ID })
      return true
    } catch {
      toast.error('Switch your wallet to Ethereum Sepolia first.')
      return false
    }
  }

  const [waitingNote, setWaitingNote] = useState(null)

  const run = async (action, functionName) => {
    if (!(await ensureSepolia())) return
    setBusy(action)
    setError(null)
    setWaitingNote(null)
    try {
      const hash = await writeContractAsync({
        address: MARKET_ADDRESS,
        abi: MARKET_ABI,
        functionName,
        args: [BigInt(ad.id)],
      })
      if (publicClient) {
        setWaitingNote('Transaction sent — waiting for Sepolia to confirm it (can take up to ~2 min)…')
        try {
          await publicClient.waitForTransactionReceipt({ hash, timeout: 120_000, pollingInterval: 2_000 })
        } catch (waitErr) {
          const receipt = await publicClient.getTransactionReceipt({ hash }).catch(() => null)
          if (receipt?.status === 'success') {
            onChanged?.()
            toast.success(
              action === 'approve' ? 'Ad approved.' :
              action === 'reject' ? 'Ad rejected.' :
              'Funds withdrawn.'
            )
            setBusy(null)
            setWaitingNote(null)
            return
          }
          if (receipt?.status === 'reverted') throw new Error('Transaction reverted on-chain.')
          setError(`Still waiting for confirmation. This usually lands within a minute — refresh the list or check back shortly. (tx: ${hash})`)
          toast.info('Transaction sent — Sepolia is just slow to confirm it. Refresh shortly.')
          setBusy(null)
          setWaitingNote(null)
          return
        }
      }
      onChanged?.()
      toast.success(
        action === 'approve' ? 'Ad approved.' :
        action === 'reject' ? 'Ad rejected.' :
        'Funds withdrawn.'
      )
    } catch (e) {
      const msg = e.shortMessage ?? e.message ?? `${action} failed`
      setError(msg)
      toast.error(msg)
    } finally {
      setBusy(null)
      setWaitingNote(null)
    }
  }

  const isPending = ad.status === 0
  const isApproved = ad.status === 1

  return (
    <div className="border-4 border-on-background bg-surface p-5 flex flex-col gap-3"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      {meta.banner && (
        <img src={meta.banner} alt="" className="w-full h-40 object-cover border-2 border-on-background" />
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-[14px] truncate">{meta.title || `Ad #${ad.id}`}</p>
          <p className="text-[11px] text-on-surface-variant font-mono truncate mt-0.5">{meta.url}</p>
          {meta.description && <p className="text-[12px] text-on-surface-variant mt-1">{meta.description}</p>}
        </div>
        <span className={`shrink-0 px-2 py-1 border font-bold text-[10px] uppercase tracking-widest ${STATUS_COLOR[ad.status]}`}>
          {STATUS_LABEL[ad.status]}
        </span>
      </div>

      <div className="flex items-center justify-between text-[11px] text-on-surface-variant flex-wrap gap-2">
        <span>{ad.durationDays?.toString()}d · {placementLabel}</span>
        <span className="font-mono font-bold">
          {ad.payToken === 0 ? `${fmt(ad.amountPaid)} ETH` : `${fmtClt(ad.amountPaid)} CLT`}
        </span>
      </div>
      <div className="flex items-center gap-4 text-[11px] text-on-surface-variant font-mono">
        <span className="flex items-center gap-1">
          <Icon name="visibility" className="text-[14px]" /> {ad.views ?? 0} views
        </span>
        <span className="flex items-center gap-1">
          <Icon name="ads_click" className="text-[14px]" /> {ad.clicks ?? 0} clicks
        </span>
      </div>
      <p className="text-[11px] text-on-surface-variant font-mono truncate">Advertiser: {ad.advertiser}</p>

      {error && <AdminAlert type="error" message={error} />}
      {waitingNote && !error && (
        <p className="text-[11px] text-on-surface-variant italic">{waitingNote}</p>
      )}

      <div className="flex gap-2 flex-wrap">
        {isPending && (
          <>
            <button onClick={() => run('approve', 'approveAd')} disabled={!isOwner || busy !== null}
              className="px-4 py-2 border-2 border-on-background bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </button>
            <button onClick={() => run('reject', 'rejectAd')} disabled={!isOwner || busy !== null}
              className="px-4 py-2 border-2 border-error text-error font-bold text-[11px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-50">
              {busy === 'reject' ? 'Rejecting…' : 'Reject'}
            </button>
          </>
        )}
        {isApproved && !ad.withdrawn && (
          <button onClick={() => run('withdraw', 'withdrawAd')} disabled={!isOwner || busy !== null}
            className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-50">
            {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw Funds'}
          </button>
        )}
        {isApproved && ad.withdrawn && (
          <p className="text-[11px] text-on-surface-variant">✓ Funds withdrawn</p>
        )}
        {ad.status === 2 && ad.refunded && (
          <p className="text-[11px] text-on-surface-variant">✓ Refunded to advertiser</p>
        )}
        {ad.status === 2 && !ad.refunded && (
          <p className="text-[11px] text-on-surface-variant">Awaiting advertiser to claim refund</p>
        )}
      </div>
    </div>
  )
}

export function AdManager() {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const toast = useToast()
  const publicClient = usePublicClient({ chainId: SEPOLIA_ID })
  const isWrongNetwork = isConnected && chainId !== SEPOLIA_ID

  const [ads, setAds] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('pending') // pending | approved | rejected | all
  const [sweepError, setSweepError] = useState(null)
  const [sweeping, setSweeping] = useState(null) // 'eth' | 'clt' | null

  const { data: owner } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'owner',
    query: { enabled: !!MARKET_ADDRESS },
  })
  const isOwner = !!address && !!owner && address.toLowerCase() === owner.toLowerCase()

  const { data: nextAdId, refetch: refetchNextAdId } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'nextAdId',
    query: { enabled: !!MARKET_ADDRESS },
  })

  const { data: ethEscrowed, refetch: refetchEthEscrowed } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'ethEscrowed',
    query: { enabled: !!MARKET_ADDRESS },
  })
  const { data: cltEscrowed, refetch: refetchCltEscrowed } = useReadContract({
    address: MARKET_ADDRESS,
    abi: MARKET_ABI,
    functionName: 'cltEscrowed',
    query: { enabled: !!MARKET_ADDRESS },
  })

  const fetchAds = useCallback(async () => {
    if (!MARKET_ADDRESS || nextAdId == null || !publicClient) return
    setLoading(true)
    try {
      const total = Number(nextAdId)
      const results = []
      for (let i = 0; i < total; i++) {
        try {
          const ad = await publicClient.readContract({
            address: MARKET_ADDRESS,
            abi: MARKET_ABI,
            functionName: 'getAd',
            args: [BigInt(i)],
          })
          results.push({ id: i, ...ad })
        } catch {}
      }
      const stats = await getAllAdsStats()
      const statsById = new Map(stats.map(s => [s.id, s]))
      const merged = results.map(ad => ({
        ...ad,
        views: statsById.get(ad.id)?.views ?? 0,
        clicks: statsById.get(ad.id)?.clicks ?? 0,
      }))
      // Newest first
      setAds(merged.reverse())
    } finally {
      setLoading(false)
    }
  }, [nextAdId, publicClient])

  useEffect(() => { fetchAds() }, [fetchAds])

  const refreshAll = () => {
    refetchNextAdId()
    refetchEthEscrowed()
    refetchCltEscrowed()
    fetchAds()
  }

  const sweep = async (kind) => {
    if (chainId !== SEPOLIA_ID) {
      try { await switchChainAsync({ chainId: SEPOLIA_ID }) } catch {
        toast.error('Switch your wallet to Ethereum Sepolia first.')
        return
      }
    }
    setSweeping(kind)
    setSweepError(null)
    try {
      const hash = await writeContractAsync({
        address: MARKET_ADDRESS,
        abi: MARKET_ABI,
        functionName: kind === 'eth' ? 'withdrawAllEth' : 'withdrawAllClt',
      })
      if (publicClient) await publicClient.waitForTransactionReceipt({ hash })
      refreshAll()
      toast.success(`${kind === 'eth' ? 'ETH' : 'CLT'} swept.`)
    } catch (e) {
      const msg = e.shortMessage ?? e.message ?? 'Sweep failed'
      setSweepError(msg)
      toast.error(msg)
    } finally {
      setSweeping(null)
    }
  }

  const filteredAds = ads.filter(ad => {
    if (filter === 'all') return true
    if (filter === 'pending') return ad.status === 0
    if (filter === 'approved') return ad.status === 1
    if (filter === 'rejected') return ad.status === 2
    return true
  })

  if (!MARKET_ADDRESS) return <NotDeployedBanner />

  return (
    <div className="flex flex-col gap-6">
      <WalletStatusBar isOwner={isOwner} owner={owner} />
      {isWrongNetwork && <WrongNetworkBanner />}
      {!isConnected ? (
        <WalletGate />
      ) : !isWrongNetwork && !isOwner ? (
        <NotOwnerBanner owner={owner} address={address} />
      ) : null}

      {/* Escrow / sweep */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="border-4 border-on-background bg-surface p-5 flex items-center justify-between gap-3"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <div>
            <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">ETH Escrowed</p>
            <p className="font-mono font-extrabold text-[20px]">{fmt(ethEscrowed)} ETH</p>
          </div>
          <button onClick={() => sweep('eth')} disabled={!isOwner || sweeping !== null}
            className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-50">
            {sweeping === 'eth' ? 'Sweeping…' : 'Sweep Free ETH'}
          </button>
        </div>
        <div className="border-4 border-on-background bg-surface p-5 flex items-center justify-between gap-3"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <div>
            <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">CLT Escrowed</p>
            <p className="font-mono font-extrabold text-[20px]">{fmtClt(cltEscrowed)} CLT</p>
          </div>
          <button onClick={() => sweep('clt')} disabled={!isOwner || sweeping !== null}
            className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-50">
            {sweeping === 'clt' ? 'Sweeping…' : 'Sweep Free CLT'}
          </button>
        </div>
      </div>
      {sweepError && <AdminAlert type="error" message={sweepError} />}
      <p className="text-[11px] text-on-surface-variant -mt-2">
        "Sweep" only pulls funds NOT tied to a pending/approved/unrefunded ad (e.g. accidental sends). Approved-ad funds are withdrawn per-ad below via "Withdraw Funds".
      </p>

      {/* Placement pricing */}
      <div className="flex flex-col gap-3">
        <h3 className="font-extrabold text-[13px] uppercase tracking-widest">Placement Pricing</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PLACEMENTS.map(p => (
            <PlacementPricingCard key={p.name} placement={p} isOwner={isOwner} onSaved={refreshAll} />
          ))}
        </div>
      </div>

      {/* Ads list */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-extrabold text-[13px] uppercase tracking-widest">Submitted Ads</h3>
          <div className="flex gap-1 border-2 border-on-background">
            {['pending', 'approved', 'rejected', 'all'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 font-bold text-[10px] uppercase tracking-widest transition-colors ${filter === f ? 'bg-on-background text-surface' : 'hover:bg-surface-container'}`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 w-full rounded" />)}
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No {filter !== 'all' ? filter : ''} ads found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredAds.map(ad => (
              <AdRow key={ad.id} ad={ad} isOwner={isOwner} onChanged={refreshAll} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}